import { useMemo, useState } from 'react';
import DataTable from '../../../reusable/DataTable/DataTable';
import StatusChip from '../../../reusable/StatusChip/StatusChip';
import Button from '../../../reusable/Button/Button';
import FilterDropdown from '../../../reusable/FilterDropdown/FilterDropdown';
import Skeleton from '../../../reusable/Skeleton/Skeleton';
import { toast } from '../../../reusable/Toast/toastStore';
import StatusFilterChips from '../StatusFilterChips';
import {
  affiliateErrorMessage,
  useGetAffiliateRetainerQuery,
  useRequestRetainerPayoutMutation,
} from '../../../store/api/affiliateApi';
import { usd, NA } from '../../../utilities/format';
import { CANNOT_REQUEST, payoutGroup, PAYOUT_GROUPS } from './data';
import { t } from '../../../i18n/i18n';
import './Retainer.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : DATE_FORMATTER.format(d);
};

// The design always shows two decimals (e.g. "$4,500.00").
const money = (value) => usd(value, { decimals: 2 });

/** The dropdown offers what the chips do — `null` is the unfiltered state both share. */
const STATUS_OPTIONS = [
  { label: 'All', value: null },
  ...PAYOUT_GROUPS.map((g) => ({ label: g.label, value: g.label })),
];

const columns = [
  { key: 'reference', header: 'Reference ID', width: 20, value: (r) => r.id, tone: () => 'primary' },
  { key: 'requestedOn', header: 'Requested On', width: 18, value: (r) => formatDate(r.created_at) },
  { key: 'amount', header: 'Amount', width: 16, value: (r) => money(r.amount), tone: () => 'primary' },
  {
    key: 'status',
    header: 'Status',
    width: 18,
    render: (r) => (
      <StatusChip tone={PAYOUT_GROUPS.find((g) => g.label === payoutGroup(r.status))?.tone}>
        {t(r.status)}
      </StatusChip>
    ),
  },
  { key: 'paidOn', header: 'Paid On', width: 16, value: (r) => formatDate(r.paid_on) },
];

/**
 * Affiliate — Retainer. Monthly retainer payout status + eligibility, then a
 * full audit trail of past payout requests — 1:19449. Everything comes from
 * `/affiliate/retainer/`; the button posts `/affiliate/retainer/request`.
 */
export default function Retainer() {
  const { data, isLoading, isError } = useGetAffiliateRetainerQuery();
  const [requestPayout, { isLoading: isRequesting }] = useRequestRetainerPayoutMutation();
  const [statusFilter, setStatusFilter] = useState(null);

  const retainer = data?.retainer ?? null;
  const payouts = useMemo(() => data?.payouts ?? [], [data]);

  const canRequest = Boolean(retainer?.can_request);
  const amount = retainer?.retainer_cost;
  const blockedReason =
    retainer?.cannot_request_message ||
    t(CANNOT_REQUEST[retainer?.cannot_request_reason]) ||
    (isError ? t('Could not load your retainer.') : null);

  /** Paid out so far — every payout that has a paid date. */
  const paid = payouts.filter((p) => p.paid_on);
  const totalPaid = paid.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  /**
   * What has to be true to request — the API's own conditions, each ticked or
   * not, rather than three fixed ticks whatever the state.
   */
  const eligibility = retainer
    ? [
        { label: t('You are on a monthly retainer.'), met: Boolean(retainer.is_retainer) },
        { label: t("This cycle's payout day has been reached."), met: Boolean(retainer.window_open) },
        { label: t('No payout requested for this cycle yet.'), met: !retainer.current_cycle_payout },
        { label: t('Payment details verified.'), met: Boolean(retainer.payment_details_verified) },
      ]
    : [];

  const statusCounts = PAYOUT_GROUPS.map((g) => ({
    ...g,
    count: payouts.filter((p) => payoutGroup(p.status) === g.label).length,
  }));
  const rows = useMemo(
    () => (statusFilter ? payouts.filter((p) => payoutGroup(p.status) === statusFilter) : payouts),
    [payouts, statusFilter]
  );

  const handleRequest = async () => {
    if (!canRequest || isRequesting) return;
    try {
      const res = await requestPayout({}).unwrap();
      toast.success(t('Retainer payout requested'), res?.message ?? t('We will confirm within one business day.'));
    } catch (err) {
      toast.error(t('Could not request payout'), affiliateErrorMessage(err));
    }
  };

  const loading = (w, h = 20) => <Skeleton w={w} h={h} />;

  return (
    <div className="affiliate-retainer">
      <div className="retainer-top">
        <div className="retainer-hero">
          <div className="retainer-hero__bg" />

          <span className="retainer-hero__badge">
            {isLoading ? t('Loading…') : canRequest ? t('Eligible now') : retainer?.is_retainer ? t('Not available yet') : t('Not on a retainer')}
          </span>
          <span className="retainer-hero__label">{t('Approved monthly retainer')}</span>
          <div className="retainer-hero__amount">
            {isLoading ? loading('160px', 40) : amount != null ? money(amount) : NA}
          </div>
          <p className="retainer-hero__copy">
            {canRequest
              ? t('Unlocked for this cycle. Request once and we confirm within one business day.')
              : blockedReason || ' '}
          </p>
          <p className="retainer-hero__stats">
            {paid.length === 1
              ? t('{{amount}} paid out across {{count}} retainer payout', { amount: money(totalPaid), count: paid.length })
              : t('{{amount}} paid out across {{count}} retainer payouts', { amount: money(totalPaid), count: paid.length })}
          </p>

          <div className="retainer-hero__meta">
            <div className="retainer-hero__meta-item">
              <span className="retainer-hero__meta-label">
                {canRequest ? t('This cycle’s payout date') : t('Next payout date')}
              </span>
              <span className="retainer-hero__meta-value">
                {isLoading
                  ? loading('90px', 14)
                  : formatDate(canRequest ? retainer?.cycle_payout_date : retainer?.next_payout_date)}
              </span>
            </div>
            <div className="retainer-hero__meta-item">
              <span className="retainer-hero__meta-label">{t('Request availability')}</span>
              <span className={`retainer-hero__meta-value ${canRequest ? 'is-available' : 'is-unavailable'}`}>
                {isLoading ? loading('80px', 14) : canRequest ? t('Available now') : t('Not available')}
              </span>
            </div>
          </div>
        </div>

        <div className="retainer-eligibility">
          <span className="retainer-eligibility__title">
            <span className="icon retainer-eligibility__icon" style={icon('affiliate-check-sm')} />
            {t('Eligibility')}
          </span>

          <ul className="retainer-eligibility__list">
            {eligibility.map((item) => (
              <li key={item.label} className={`retainer-eligibility__item${item.met ? '' : ' is-unmet'}`}>
                <span className="retainer-eligibility__check">
                  <span
                    className="icon"
                    style={icon(item.met ? 'affiliate-check-circle' : 'warning-triangle')}
                  />
                </span>
                {item.label}
              </li>
            ))}
          </ul>

          <Button
            variant="light"
            className="retainer-eligibility__submit"
            height={38}
            disabled={!canRequest || isRequesting}
            loading={isRequesting}
            onClick={handleRequest}
          >
            {amount != null ? t('Request {{amount}} payout', { amount: money(amount) }) : t('Request payout')}
          </Button>

          <p className="retainer-eligibility__footnote">
            {canRequest || !blockedReason
              ? t('You can request this payout once for the current cycle.')
              : blockedReason}
          </p>
        </div>
      </div>

      <div className="affiliate-retainer__toolbar">
        <StatusFilterChips counts={statusCounts} value={statusFilter} onChange={setStatusFilter} />
      </div>

      <DataTable
        icon="affiliate-recent-requests"
        title="Request History"
        subtitle="Complete audit trail of your payout requests"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        pageSize={10}
        loading={isLoading}
        emptyMessage={
          isError
            ? t('Could not load your payout requests')
            : statusFilter
              ? t('No payout requests with this status')
              : t('No retainer payouts requested yet')
        }
        searchPlaceholder="Search requests"
        actions={
          <FilterDropdown
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
            label="Filters"
          />
        }
      />
    </div>
  );
}
